/**
 * Guided connector configuration catalogue (§7, §14.3).
 *
 * Each connector declares the fields an operator actually needs to fill —
 * labelled, typed, with help and validation — so configuring a channel is a
 * form, not a hand-authored JSON blob. Field KEYS mirror exactly what each
 * connector reads from `context.credentials` / `context.settings` in its
 * factory (packages/connectors/<key>/src/index.ts); labels and help are ours.
 *
 * Credential fields are write-only (§14.3): rendered as password inputs, sent
 * once, never read back. Settings are non-secret configuration.
 *
 * This lives client-side for now; the natural next step is for each connector
 * to publish this schema from the SDK so there is a single source of truth. The
 * shapes here are kept faithful to the connector code so that move is mechanical.
 */

export interface ConnectorField {
  readonly key: string;
  readonly label: string;
  /** Rendered as a password input and treated as a credential (write-only). */
  readonly secret?: boolean;
  readonly required?: boolean;
  readonly placeholder?: string;
  readonly help?: string;
  /** Present -> rendered as a <select>. */
  readonly options?: readonly string[];
  readonly defaultValue?: string;
}

export interface ChannelFormat {
  readonly width: number;
  readonly height: number;
}

export interface ConnectorForm {
  readonly credentials: readonly ConnectorField[];
  readonly settings: readonly ConnectorField[];
  /** A short line explaining where to obtain the credentials. */
  readonly credentialsHint?: string;
  /** Accepted creative formats, shown as a preview for destinations. */
  readonly formats?: readonly ChannelFormat[];
  readonly acceptedFileTypes?: readonly string[];
  readonly maxFileSizeBytes?: number;
  /** Connectors that take no configuration at all (e.g. file upload). */
  readonly note?: string;
}

const TOKEN = (label: string, help?: string): ConnectorField => ({
  key: 'accessToken',
  label,
  secret: true,
  required: true,
  ...(help === undefined ? {} : { help }),
});

const SOCIAL_FORMATS: readonly ChannelFormat[] = [
  { width: 1080, height: 1080 },
  { width: 1080, height: 1350 },
];

export const CONNECTOR_FORMS: Record<string, ConnectorForm> = {
  'csv-import': {
    credentials: [],
    settings: [],
    note: 'No credentials needed. Upload CSV/JSON files, then add this as a Source below and map its columns to Asset fields.',
  },

  'webhook-receiver': {
    credentials: [
      {
        key: 'secret',
        label: 'Signing secret',
        secret: true,
        required: true,
        help: 'HMAC secret used to verify that inbound payloads are genuine.',
      },
    ],
    settings: [
      { key: 'signatureHeader', label: 'Signature header', placeholder: 'X-Signature' },
      {
        key: 'algorithm',
        label: 'HMAC algorithm',
        options: ['sha256', 'sha1'],
        defaultValue: 'sha256',
      },
      {
        key: 'encoding',
        label: 'Signature encoding',
        options: ['hex', 'base64'],
        defaultValue: 'hex',
      },
      { key: 'idField', label: 'Record id field', placeholder: 'id' },
      {
        key: 'recordsPath',
        label: 'Records path',
        placeholder: 'data.items',
        help: 'Dot-path to the array of records inside the payload.',
      },
    ],
  },

  shopify: {
    credentialsHint: 'Shopify admin → Apps → Develop apps → your app → Admin API access token.',
    credentials: [
      TOKEN('Admin API access token', 'Starts with shpat_.'),
      {
        key: 'webhookSecret',
        label: 'Webhook signing secret',
        secret: true,
        help: 'Only needed to receive Shopify webhooks.',
      },
    ],
    settings: [
      {
        key: 'shopDomain',
        label: 'Shop domain',
        required: true,
        placeholder: 'my-store.myshopify.com',
      },
      { key: 'apiVersion', label: 'API version', placeholder: '2024-10', defaultValue: '2024-10' },
    ],
  },

  'tiktok-content': {
    credentials: [TOKEN('Access token')],
    settings: [
      {
        key: 'privacyLevel',
        label: 'Privacy level',
        options: ['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'SELF_ONLY'],
        defaultValue: 'PUBLIC_TO_EVERYONE',
      },
    ],
    formats: [{ width: 1080, height: 1920 }],
    acceptedFileTypes: ['image/jpeg', 'image/webp', 'video/mp4'],
  },

  'tiktok-shop': {
    credentials: [
      TOKEN('Access token'),
      { key: 'appKey', label: 'App key', required: true },
      { key: 'appSecret', label: 'App secret', secret: true, required: true },
      { key: 'refreshToken', label: 'Refresh token', secret: true },
    ],
    settings: [
      { key: 'shopId', label: 'Shop id', required: true },
      { key: 'shopCipher', label: 'Shop cipher', help: 'From TikTok Shop authorised_shops.' },
      { key: 'apiVersion', label: 'API version', placeholder: '202309' },
    ],
  },

  'google-merchant': {
    credentialsHint: 'Google Cloud OAuth client with Content API for Shopping access.',
    credentials: [
      { key: 'refreshToken', label: 'OAuth refresh token', secret: true, required: true },
      { key: 'clientId', label: 'OAuth client id', required: true },
      { key: 'clientSecret', label: 'OAuth client secret', secret: true, required: true },
    ],
    settings: [
      { key: 'merchantId', label: 'Merchant Centre id', required: true },
      { key: 'targetCountry', label: 'Target country', placeholder: 'GB', defaultValue: 'GB' },
      { key: 'contentLanguage', label: 'Content language', placeholder: 'en', defaultValue: 'en' },
    ],
    formats: [{ width: 1200, height: 1200 }],
    acceptedFileTypes: ['image/jpeg', 'image/png'],
  },

  'meta-graph': {
    credentialsHint: 'Meta App → Graph API long-lived token with the pages/instagram permissions.',
    credentials: [
      TOKEN('Access token'),
      {
        key: 'appSecret',
        label: 'App secret',
        secret: true,
        help: 'For appsecret_proof; optional.',
      },
    ],
    settings: [
      {
        key: 'target',
        label: 'Surface',
        options: ['instagram', 'facebook'],
        required: true,
        defaultValue: 'instagram',
      },
      { key: 'igUserId', label: 'Instagram user id', help: 'Required for Instagram.' },
      { key: 'pageId', label: 'Facebook Page id', help: 'Required for Facebook.' },
      { key: 'apiVersion', label: 'Graph API version', placeholder: 'v21.0' },
    ],
    formats: SOCIAL_FORMATS,
    acceptedFileTypes: ['image/jpeg', 'image/png'],
  },

  linkedin: {
    credentialsHint: 'LinkedIn Developer app → OAuth token with the marketing/organization scopes.',
    credentials: [
      TOKEN('Access token'),
      { key: 'clientId', label: 'OAuth client id', help: 'Only needed to refresh the token.' },
      {
        key: 'clientSecret',
        label: 'OAuth client secret',
        secret: true,
        help: 'Only needed to refresh the token.',
      },
    ],
    settings: [
      {
        key: 'authorId',
        label: 'Author URN',
        required: true,
        placeholder: 'urn:li:organization:12345',
      },
      {
        key: 'apiVersion',
        label: 'API version (YYYYMM)',
        required: true,
        placeholder: '202506',
        help: 'A currently-supported version — an expired one returns a 426 that looks like an auth failure.',
      },
    ],
    formats: [
      { width: 1200, height: 627 },
      { width: 1080, height: 1080 },
    ],
    acceptedFileTypes: ['image/jpeg', 'image/png'],
    maxFileSizeBytes: 10 * 1024 * 1024,
  },

  pinterest: {
    credentials: [TOKEN('Access token')],
    settings: [{ key: 'boardId', label: 'Board id', required: true }],
    formats: [{ width: 1000, height: 1500 }],
    acceptedFileTypes: ['image/jpeg', 'image/png'],
  },

  wordpress: {
    credentialsHint: 'WordPress → Users → Profile → Application Passwords.',
    credentials: [
      { key: 'username', label: 'Username', required: true },
      { key: 'applicationPassword', label: 'Application password', secret: true, required: true },
    ],
    settings: [
      { key: 'siteUrl', label: 'Site URL', required: true, placeholder: 'https://example.com' },
      { key: 'postType', label: 'Post type', options: ['post', 'page'], defaultValue: 'post' },
      {
        key: 'defaultStatus',
        label: 'Default status',
        options: ['draft', 'publish', 'pending'],
        defaultValue: 'draft',
      },
    ],
  },

  twitter: {
    credentialsHint:
      'X developer portal → OAuth 2.0 user access token with tweet.write + media.write.',
    credentials: [TOKEN('OAuth 2.0 access token')],
    settings: [],
    formats: [{ width: 1600, height: 900 }],
    acceptedFileTypes: ['image/jpeg', 'image/png'],
  },

  bluesky: {
    credentialsHint: 'Bluesky → Settings → App Passwords (never your main password).',
    credentials: [
      { key: 'identifier', label: 'Handle', required: true, placeholder: 'name.bsky.social' },
      { key: 'password', label: 'App password', secret: true, required: true },
      {
        key: 'pdsUrl',
        label: 'PDS URL',
        placeholder: 'https://bsky.social',
        defaultValue: 'https://bsky.social',
      },
    ],
    settings: [],
    formats: [{ width: 1200, height: 675 }],
    acceptedFileTypes: ['image/jpeg', 'image/png'],
  },

  threads: {
    credentials: [TOKEN('Access token')],
    settings: [{ key: 'userId', label: 'Threads user id', required: true }],
    formats: SOCIAL_FORMATS,
    acceptedFileTypes: ['image/jpeg', 'image/png'],
  },

  youtube: {
    credentialsHint: 'Google Cloud OAuth client with YouTube Data API v3 access.',
    credentials: [
      TOKEN('OAuth access token'),
      { key: 'clientId', label: 'OAuth client id', help: 'For token refresh.' },
      {
        key: 'clientSecret',
        label: 'OAuth client secret',
        secret: true,
        help: 'For token refresh.',
      },
    ],
    settings: [],
    formats: [{ width: 1280, height: 720 }],
    acceptedFileTypes: ['video/mp4'],
  },

  'google-analytics': {
    credentialsHint:
      'Google Cloud OAuth client with the Analytics Data API and Admin API enabled (analytics.readonly).',
    credentials: [
      TOKEN('OAuth access token'),
      { key: 'clientId', label: 'OAuth client id', help: 'For token refresh.' },
      {
        key: 'clientSecret',
        label: 'OAuth client secret',
        secret: true,
        help: 'For token refresh.',
      },
    ],
    settings: [
      {
        key: 'ga4PropertyId',
        label: 'GA4 property id',
        help: 'Usually set by choosing a property after connecting.',
      },
    ],
  },

  snapchat: {
    credentials: [TOKEN('Access token')],
    settings: [{ key: 'adAccountId', label: 'Ad account id', required: true }],
    formats: [{ width: 1080, height: 1920 }],
    acceptedFileTypes: ['image/jpeg', 'image/png', 'video/mp4'],
  },

  whatsapp: {
    credentialsHint:
      'Meta App → WhatsApp → a permanent System User access token with the messaging permissions.',
    credentials: [
      TOKEN('Access token'),
      { key: 'appId', label: 'App id', help: 'Only needed to refresh the token.' },
      {
        key: 'appSecret',
        label: 'App secret',
        secret: true,
        help: 'Only needed to refresh the token.',
      },
    ],
    settings: [
      {
        key: 'phoneNumberId',
        label: 'Phone number id',
        required: true,
        help: 'From WhatsApp → API Setup.',
      },
      {
        key: 'templateName',
        label: 'Template name',
        required: true,
        help: 'A pre-approved message template — WhatsApp sends templates, not free posts.',
      },
      { key: 'languageCode', label: 'Template language', placeholder: 'en_US' },
      {
        key: 'recipient',
        label: 'Recipient (E.164)',
        required: true,
        help: 'The opted-in number to message, e.g. 447700900123.',
      },
      { key: 'apiVersion', label: 'Graph API version', placeholder: 'v21.0' },
    ],
    formats: [],
    acceptedFileTypes: ['image/jpeg', 'image/png'],
  },
};

/** The form for a connector, or a permissive default for anything unlisted. */
export function connectorForm(key: string): ConnectorForm {
  return (
    CONNECTOR_FORMS[key] ?? {
      credentials: [{ key: 'accessToken', label: 'Access token', secret: true }],
      settings: [],
    }
  );
}
